/** RFC 0279 S7: which installed packages the registry has a newer version of. */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  type AppCapabilities,
  type AppDoc,
  type AppSummary,
  isNewerVersion,
  type PackageRelease,
  type Registry,
  registryClient,
  releaseReachesMore,
} from '@mod/core/model'
import { useEffect, useMemo, useState } from 'react'
import { useDocuments } from '../../services/world/repo/useDocument'

export interface PackageUpdate {
  app: AppSummary
  from: string
  to: string
  releaseId: string
  release: PackageRelease
  /** What the new version reaches that the installed one does not: added
   *  reads and writes, and `realm`/`sync` when it newly runs in the window or
   *  newly syncs the project's documents (RFC 0279 3f). */
  reaches: {
    reads: string[]
    writes: string[]
    realm?: 'host'
    sync?: 'documents'
  }
}

export interface InstalledPackage {
  app: AppSummary
  installedFrom?: { lineageId: string; version: string }
  capabilities?: AppCapabilities
  /** Where the installed version runs; absent is sandboxed. */
  realm?: 'host'
  /** Whether the installed version already syncs documents. */
  sync?: 'documents'
}

/**
 * Ask the registry for the latest of each installed lineage.
 *
 * One package that fails to answer is skipped rather than failing the list: a
 * withdrawn lineage should not hide the updates for everything else.
 */
export async function findUpdates(
  installed: InstalledPackage[],
  registry: Pick<Registry, 'package' | 'release'>,
): Promise<PackageUpdate[]> {
  const found = await Promise.all(
    installed.map(async ({ app, installedFrom, capabilities, realm, sync }) => {
      if (!installedFrom) return null
      try {
        const item = await registry.package(installedFrom.lineageId)
        if (!isNewerVersion(item.latest.version, installedFrom.version))
          return null
        const release = await registry.release(item.latest.releaseId)
        return {
          app,
          from: installedFrom.version,
          to: item.latest.version,
          releaseId: item.latest.releaseId,
          release,
          reaches: releaseReachesMore(
            {
              reads: capabilities?.reads ?? [],
              writes: capabilities?.writes ?? [],
              ...(realm ? { realm } : {}),
              ...(sync ? { sync } : {}),
            },
            release,
          ),
        }
      } catch {
        return null
      }
    }),
  )
  return found.filter((u): u is PackageUpdate => u !== null)
}

const registry = registryClient({ getAuthToken: () => '' })

/**
 * The updates for what is installed, checked when `active` turns on (Packages
 * opens) and again after `bump` changes (an update was taken).
 */
export function usePackageUpdates(
  apps: AppSummary[],
  active: boolean,
  bump = 0,
): PackageUpdate[] {
  const docs = useDocuments<AppDoc>(apps.map((a) => a.id as AnyDocumentId))
  const installed = useMemo(
    () =>
      apps.map((app) => {
        const doc = docs.get(app.id)
        return {
          app,
          installedFrom: doc?.installedFrom,
          capabilities: doc?.capabilities,
          realm: doc?.realm,
          sync: doc?.sync,
        }
      }),
    [apps, docs],
  )
  // Keyed on what decides the answer, so a re-render with the same installs
  // does not ask the registry again.
  const key = installed
    .map((i) => `${i.app.id}@${i.installedFrom?.version ?? ''}`)
    .join(',')
  const [updates, setUpdates] = useState<PackageUpdate[]>([])
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `installed`
  useEffect(() => {
    if (!active) return
    let live = true
    findUpdates(installed, registry).then((found) => {
      if (live) setUpdates(found)
    })
    return () => {
      live = false
    }
  }, [active, key, bump])
  return active ? updates : []
}
